/**
 * Tabs / TabButton
 *
 * Figma:
 * - Design System 0.1 > Tab (group)
 * - Design System 0.1 > Tab Button (Active=On/Off)
 *
 * 두 가지 사용 모드:
 * - `Tabs` 그룹 + `TabsTrigger`: Radix Tabs primitive 기반 — 활성 상태 관리, 키보드
 *   네비게이션, ARIA 속성을 Radix 가 자동으로 처리한다. `TabsTrigger` 는 Radix
 *   `data-state` 를 `TabButton` 의 `active` prop 으로 변환해 시각을 적용한다.
 * - `TabButton`: Radix 컨텍스트 없이 사용하는 독립 컴포넌트 — 활성 상태는 caller 가
 *   `active` prop 으로 제어. URL 기반 라우팅이나 커스텀 탭 구현 등에 사용.
 *
 * 인터랙션 visual(hover/focus/pressed)은 `DecorateInteraction` 의 normal/neutral
 * variant 가 ::before overlay 로 처리한다.
 */

import * as TabsPrimitive from "@radix-ui/react-tabs";
import { forwardRef } from "react";
import { cn } from "@/lib/utils";
import { DecorateInteraction } from "../decorate/interaction";

// ============================================
// TabButton — 시각 단위 (Radix 컨텍스트 없이도 사용 가능)
// ============================================

export interface TabButtonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 활성 상태 — true 면 Label/Strong 텍스트 + 2px 하단 underline */
  active?: boolean;
}

/** 활성/비활성 시각은 `active` prop 으로만 분기한다. */
export const TabButton = forwardRef<HTMLDivElement, TabButtonProps>(
  (
    {
      active = false,
      className,
      children,
      onMouseLeave: userOnMouseLeave,
      ...props
    },
    ref,
  ) => (
    <DecorateInteraction asChild variant="normal" color="neutral">
      <div
        ref={ref}
        {...props}
        data-state={active ? "active" : "inactive"}
        onMouseLeave={(event) => {
          // 마우스 클릭으로 생긴 focus 잔상 제거 — element 를 blur 해 focus-within
          // 을 풀어준다. 키보드 포커스(:focus-visible)는 유지해 a11y 보장.
          if (!event.currentTarget.matches(":focus-visible")) {
            event.currentTarget.blur();
          }
          userOnMouseLeave?.(event);
        }}
        className={cn(
          "-mb-px flex items-center justify-center rounded-none px-3 min-w-10",
          className,
        )}
      >
        <div
          className={cn(
            "py-3 flex items-center justify-center border-b-2 body-2-normal-semibold",
            active
              ? "border-label-strong text-label-strong"
              : "border-transparent text-label-assistive",
          )}
        >
          {children}
        </div>
      </div>
    </DecorateInteraction>
  ),
);
TabButton.displayName = "TabButton";

// ============================================
// Tabs (Radix Tabs primitive 기반)
// ============================================

/** Tabs root — Radix `Tabs.Root` 그대로 노출 */
export const Tabs = TabsPrimitive.Root;

/** Tabs 트리거 행 — 하단 구분선 기본 포함 */
export const TabsList = forwardRef<
  React.ComponentRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn("relative flex items-center", className)}
    {...props}
  />
));
TabsList.displayName = "TabsList";

/**
 * Radix Tabs 컨텍스트 안의 탭 트리거.
 * Radix 가 주입하는 `data-state` 를 `active` prop 으로 변환해 TabButton 에 전달한다.
 */
const TabsTriggerSlot = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    "data-state"?: "active" | "inactive";
  }
>(({ children, className, "data-state": dataState, ...props }, ref) => (
  <TabButton
    ref={ref}
    active={dataState === "active"}
    className={className}
    {...props}
  >
    {children}
  </TabButton>
));
TabsTriggerSlot.displayName = "TabsTriggerSlot";

export const TabsTrigger = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ children, className, ...props }, ref) => (
  <TabsPrimitive.Trigger {...props} asChild>
    <TabsTriggerSlot ref={ref} className={className}>
      {children}
    </TabsTriggerSlot>
  </TabsPrimitive.Trigger>
));
TabsTrigger.displayName = "TabsTrigger";

/** 탭 컨텐츠 패널 */
export const TabsContent = forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn("focus-visible:outline-none", className)}
    {...props}
  />
));
TabsContent.displayName = "TabsContent";
